<script lang="ts">
  import { timeFormat } from 'd3-time-format';
  import ElectionFlow from '../HistoricalElections/ElectionFlow.svelte';
  import historicalResults from '$lib/data/election-data.json';
  import {
    coalitionSettings,
    comparisonBlocMeta,
    comparisonPartyMeta,
  } from '../HistoricalElections/party-meta.js';
  import { calculatePartyAggregates } from '$lib/utils';
  import { parties as pollParties, seatConfig } from '$lib/constants/crosswalk';
  import { pollPartyToElectionParty } from './party-crosswalk.js';
  import {
    antiNetanyahuBloc,
    minSeatsToShow,
  } from '$lib/constants/politicalBlocs';

  interface Props {
    title?: string;
    subHead?: string;
    notes?: string;
    unassignedNote?: string;
    source?: string;
    /** Note template with a `{date}` token, e.g. `"Data as of {date}."` */
    dataAsOf?: string;
    latestPollDate?: Date;
    altText?: string;
    /** Caps the chart's width, e.g. '700px'. Unset renders at its container's full width. */
    maxWidth?: string;
    structuredData: { trends: Record<string, [Date, number][]> };
  }

  let {
    title = '',
    subHead = '',
    notes = '',
    unassignedNote = '',
    source = '',
    dataAsOf = '',
    latestPollDate,
    altText = '',
    maxWidth = '660px',
    structuredData,
  }: Props = $props();

  const formatDate = timeFormat('%Y-%m-%d');
  const formatDisplayDate = timeFormat('%B %-d');

  // The actual 2022 result — always the same election flagged in
  // `coalitionSettings.electionDate` — to compare estimates against.
  const priorElectionRows = historicalResults.filter(
    (d) => d.date === coalitionSettings.electionDate
  );

  // Resolves a poll-tracker party key to the label it should render under on
  // this chart — its 2022-lineage name where one exists, or its own
  // poll-tracker label otherwise. Shared between the row-building below and
  // the anti-Netanyahu outline, which needs the same key-to-label mapping.
  const toElectionLabel = (key: string): string =>
    pollPartyToElectionParty[key] ||
    pollParties.find((p) => p.key === key)?.label ||
    key;

  // TEMPORARY — Reservists and People of Israel have no confirmed real-world
  // bloc yet, so they don't get their own bars at all here. Instead, a single
  // "Other parties" row (coloured/positioned via the `unassigned` entry in
  // `comparisonDefinitions`, HistoricalElections/party-meta.js) is sized as
  // whatever's left to reach 120 seats after every other displayed party —
  // not their own raw polling estimate. Each party's estimate comes from an
  // independent trend fit with no cross-party normalization, so displayed
  // seats don't naturally sum to 120 on their own; this reconciles the row to
  // the real Knesset size instead of leaving it over/under.
  // To revert once more polling clarifies where they belong: delete this
  // constant and go back to mapping every aggregate (including these two)
  // through `toElectionLabel`, and drop the `unassigned` entry in
  // `comparisonDefinitions`.
  const otherPartyKeys = ['reservists', 'people_of_israel'];

  // Latest polling average per party, converted into the same row shape as
  // `historicalResults`, so it can be plotted as a second "election" bar.
  // Parties polling at or below the shared `minSeatsToShow` threshold
  // (see `$lib/constants/politicalBlocs`) are dropped entirely — no bar
  // segment, and excluded from the row's total (they're folded into "Other
  // parties" via the 120-seat reconciliation above instead).
  const estimateRows = $derived.by(() => {
    const aggregates = calculatePartyAggregates(structuredData.trends);

    const rows = aggregates
      .filter((d: any) => !otherPartyKeys.includes(d.party))
      .map((d: any) => {
        const seats = Math.round(d.value);
        if (seats <= minSeatsToShow) return null;
        return {
          date: formatDate(d.date),
          year: d.date.getFullYear(),
          party: toElectionLabel(d.party),
          seats,
        };
      })
      .filter(Boolean);

    const otherSeats =
      seatConfig.totalSeats -
      rows.reduce((sum: number, d: any) => sum + d.seats, 0);

    if (otherSeats > 0 && aggregates.length) {
      const date = (aggregates[0] as any).date;
      rows.push({
        date: formatDate(date),
        year: date.getFullYear(),
        party: 'Other parties',
        seats: otherSeats,
      });
    }

    return rows;
  });

  const data = $derived([...priorElectionRows, ...estimateRows]);

  const dataAsOfText = $derived(
    latestPollDate && dataAsOf ?
      dataAsOf.replace('{date}', formatDisplayDate(latestPollDate))
    : ''
  );

  // Notes render above the source line, so the date note leads the note copy.
  const fullNotes = $derived([dataAsOfText, notes].filter(Boolean).join(' '));

  // Labels the estimate row without hardcoding the date it lands on.
  const electionAnnotations = $derived(
    estimateRows.length ? { [estimateRows[0].date]: 'Estimated seats' } : {}
  );

  // Dashed outline + label marking the anti-Netanyahu bloc on the polling
  // estimate row — a curated alliance of specific parties (see
  // `antiNetanyahuBloc` in `$lib/constants/politicalBlocs`), not every party
  // in the opposition-leaning blocs. A member below the seat threshold simply
  // won't have a bar segment in `estimateRows`, so it's naturally excluded
  // from the outline's bounds too.
  const oppositionSettings = $derived(
    estimateRows.length ?
      {
        show: true,
        electionDate: estimateRows[0].date,
        parties: antiNetanyahuBloc.map((member) => toElectionLabel(member.key)),
        label: 'Anti-Netanyahu parties',
        stroke: '#111111',
        strokeWidth: 2.5,
      }
    : { show: false }
  );
</script>

<div class="election-comparison" style:max-width={maxWidth}>
  <ElectionFlow
    {data}
    {electionAnnotations}
    {oppositionSettings}
    blocMeta={comparisonBlocMeta}
    partyMeta={comparisonPartyMeta}
    partyNotes={{
      '2022-11-01': { 'Religious Zionism': { short: 'RZ & JP' } },
    }}
    {unassignedNote}
    rowGap={64}
    topPadding={65}
    sortOrder="desc"
    legendPosition="bottom"
    markMajority
    fillRemainder
    {title}
    dek={subHead}
    notes={fullNotes}
    {source}
    {altText}
  />
</div>

<style>
  .election-comparison {
    margin: 0 auto;
  }
</style>
